import { FC, useEffect, useMemo, useState } from 'react';

import { pastThanksCards } from '@service/giftStore/state/giftThanksPrint.atom';
import { CheckIcon, DuplicateIcon } from '@heroicons/react/outline';
import {
  GiftOrder,
  GiftOrderDeliveryMode,
  GiftOrderDocStatus,
  GiftOrderPaymentStatus,
} from '@shared/api/giftOrder/giftOrder.interface';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';
import { formatDate } from '@shared/utils/formatDate';
import { formatPrice } from '@shared/utils/formatPrice';

/**
 * 상가(fevent) 단위 답례품 주문 이력 — 전화 상담용 공용 컴포넌트.
 *
 * 고객이 전화 전에 이미 결제를 시도했을 수 있고, 다른 가족의 주문도 함께 보이면
 * 상담이 쉬워진다. 그래서 상주(member)가 아니라 상가(fevent) 단위로 조회하고,
 * 결제창을 닫아 지워진 시도(docStatus Deleted)도 "무엇을 사려 했는지" 보이도록
 * 포함한다. 사용처: 고객검색 행 펼침 + 대리주문 모드 스토어 옆 패널.
 */

export type FeventOrder = GiftOrder;

const HISTORY_LIMIT = 20;
const HISTORY_SELECT =
  'paymentDetail receiverName receiverPhoneNumber orderDetails deliveryMode bulkDeliveryAddress thanksPrint thanksPrints viaPhoneOrder docStatus createdAt';

//* 정규식·문자열이 그대로 실려나가도록 직접 인코딩 (select는 stringify 금지)
const adminHistoryQuery = (query: Record<string, unknown>) =>
  Object.entries(query)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${key}=${encodeURIComponent(
          typeof value === 'string' ? value : JSON.stringify(value),
        )}`,
    )
    .join('&');

const historyFromYmd = () => {
  const d = new Date();
  d.setDate(d.getDate() - 365);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
};

//* 검색 결과에 "주문내역(n)"을 미리 표시하기 위한 캐시.
//* 같은 상가를 행 배지·펼침에서 중복 조회하지 않도록 결과를 담아둔다.
const historyCache = new Map<string, FeventOrder[]>();

/** 캐시에 있으면 즉시 반환(없으면 undefined) — 배지 렌더용 */
export const getCachedFeventOrders = (feventId: string): FeventOrder[] | undefined =>
  historyCache.get(feventId);

/**
 * 상담용 이력 조회 — 전용 라우트 우선 (Admin·GiftAgent 공용).
 * 서버가 필터·select·정렬을 고정하고 `{ fevent: 1 }` 인덱스를 타며,
 * **docStatus로 거르지 않아** 결제 중단 시도까지 내려온다.
 * 미배포 서버(404)일 때만 Admin은 제네릭으로 폴백한다.
 */
export const fetchFeventOrderHistory = async (
  feventId: string,
  canUseAdminFallback: boolean,
): Promise<FeventOrder[]> => {
  const cached = historyCache.get(feventId);
  if (cached) return cached;

  let data: FeventOrder[];
  try {
    ({ data } = await api_.get<FeventOrder[]>(
      `/gift-agent/gift-order/history?fevent=${feventId}`,
    ));
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status !== 404 || !canUseAdminFallback) throw err;
    //* 구서버 폴백. from 명시 필수 — 생략하면 기본 조회기간(최근 7일)에 잘린다
    ({ data } = await api_.get<FeventOrder[]>(
      `/admin/gift-order?${adminHistoryQuery({
        filter: { fevent: feventId },
        options: { sort: { createdAt: -1 }, limit: HISTORY_LIMIT },
        select: HISTORY_SELECT,
      })}&from=${historyFromYmd()}`,
    ));
  }
  historyCache.set(feventId, data);
  return data;
};

//* 선조회 동시 실행 수. 검색 1회가 상가 50건이면 그만큼의 조회가 한꺼번에 나가
//* 서버에 스파이크를 만든다. 배지는 목록이 뜬 뒤 채워지는 부가 정보라 조금 늦어도 된다.
const PREFETCH_CONCURRENCY = 4;

/** 검색 직후 건수 배지를 채우기 위한 일괄 선조회 — 실패한 상가는 조용히 건너뛴다 */
export const prefetchFeventOrderCounts = async (
  feventIds: string[],
  canUseAdminFallback: boolean,
): Promise<Map<string, number>> => {
  const counts = new Map<string, number>();
  let cursor = 0;

  //* 워커 n개가 같은 큐를 나눠 먹는다 (동시 실행이 항상 n 이하)
  const worker = async () => {
    for (let index = cursor++; index < feventIds.length; index = cursor++) {
      const feventId = feventIds[index];
      if (!feventId) continue;
      try {
        const orders = await fetchFeventOrderHistory(feventId, canUseAdminFallback);
        counts.set(feventId, orders.length);
      } catch (err) {
        console.error(err);
      }
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(PREFETCH_CONCURRENCY, feventIds.length) }, worker),
  );
  return counts;
};

const digitsOnly = (value?: string) => (value ?? '').replace(/\D/g, '');

/**
 * 이 주문이 **그 상주 본인**의 주문인지 — 주문자 이름 + 전화번호 완전일치.
 *
 * 스토어 게스트(user)가 이름+전화번호로 만들어지므로, 둘이 모두 같으면 같은 사람의
 * 주문으로 볼 수 있다. 같은 상가(fevent)엔 다른 상주(가족)의 주문도 섞여 있어,
 * 본인 주문만 위로 올리고 표기를 구분하며 자동 프리필도 이것만 쓴다.
 */
export const isSameMemberOrder = (
  order: Pick<FeventOrder, 'receiverName' | 'receiverPhoneNumber'>,
  memberName?: string,
  memberPhone?: string,
): boolean => {
  const name = memberName?.trim();
  const phone = digitsOnly(memberPhone);
  if (!name || !phone) return false;
  const orderPhone = digitsOnly(order.receiverPhoneNumber);
  return orderPhone === phone && (order.receiverName ?? '').trim() === name;
};

//* 상담용 상태 표기 — 결제창을 닫은 시도(Deleted)와 미완료도 구분해 보여준다
const historyStatusChip = (order: FeventOrder): { text: string; cls: string } => {
  if (order.docStatus === GiftOrderDocStatus.Deleted)
    return { text: '결제 중단', cls: 'bg-gray-100 text-gray-500' };
  const status = order.paymentDetail.status;
  if (
    status === GiftOrderPaymentStatus.NotPaid ||
    status === GiftOrderPaymentStatus.Waiting
  )
    return order.paymentDetail.vBank
      ? { text: '입금대기', cls: 'bg-amber-100 text-amber-700' }
      : { text: '결제 미완료', cls: 'bg-amber-50 text-amber-600' };
  if (status === GiftOrderPaymentStatus.Paid)
    return { text: '결제완료', cls: 'bg-green-100 text-green-700' };
  if (status === GiftOrderPaymentStatus.Confirmed)
    return { text: '구매확정', cls: 'bg-green-100 text-green-700' };
  if (status === GiftOrderPaymentStatus.CancelRequested)
    return { text: '취소요청', cls: 'bg-red-100 text-red-700' };
  return { text: '취소됨', cls: 'bg-gray-100 text-gray-500' };
};

const historyDateTime = (date: Date) =>
  formatDate(date, { dateSeparater: '.', contains: { hours: true, minutes: true } });

//* 감사글·주소를 그대로 옮겨 담기 위한 복사 버튼 (누른 뒤 잠깐 "복사됨")
const CopyButton: FC<{ text: string; label?: string }> = function CopyButton({
  text,
  label = '복사',
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setCopied(true);
      }}
      className={`flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium transition-colors ${
        copied
          ? 'bg-green-100 text-green-700'
          : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
      }`}
    >
      {copied ? (
        <CheckIcon className="h-3 w-3" />
      ) : (
        <DuplicateIcon className="h-3 w-3" />
      )}
      {copied ? '복사됨' : label}
    </button>
  );
};

//* 지금 열려 있는 주문서에 이 내용을 그대로 넣는 버튼 (대리주문 중에만 노출).
//* 자동으로 채우지 않고 직원이 내용을 보고 누르게 하는 것이 핵심 — 다른 가족의
//* 감사글·주소가 저절로 들어가 있는 상황을 만들지 않는다.
const ApplyButton: FC<{ onApply: () => void }> = function ApplyButton({ onApply }) {
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (!applied) return;
    const timer = setTimeout(() => setApplied(false), 1500);
    return () => clearTimeout(timer);
  }, [applied]);

  return (
    <button
      onClick={() => {
        onApply();
        setApplied(true);
      }}
      className={`rounded px-1.5 py-0.5 text-xs font-medium transition-colors ${
        applied
          ? 'bg-green-100 text-green-700'
          : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
      }`}
    >
      {applied ? '채웠어요' : '주문서에 채우기'}
    </button>
  );
};

//* 라벨 + 값 + 복사/채우기 버튼 한 블록 (감사글·배송지 공통 틀)
const CopyableField: FC<{
  label: string;
  copyText: string;
  onApply?: () => void;
  children: React.ReactNode;
}> = function CopyableField({ label, copyText, onApply, children }) {
  return (
    <div className="rounded-md bg-gray-50 px-2.5 py-2">
      <div className="flex items-center justify-between gap-1">
        <span className="text-xs font-medium text-gray-500">{label}</span>
        <div className="flex shrink-0 items-center gap-1">
          {onApply && <ApplyButton onApply={onApply} />}
          <CopyButton text={copyText} />
        </div>
      </div>
      <div className="mt-1 text-sm leading-relaxed text-gray-800">{children}</div>
    </div>
  );
};

interface HistoryApplyHandlers {
  /** 이 주문의 감사글을 지금 열려 있는 주문서에 채운다 (대리주문 중에만 전달됨) */
  onApplyThanks?: (order: FeventOrder) => void;
  /** 이 주문의 배송지를 지금 열려 있는 주문서에 채운다 */
  onApplyAddress?: (order: FeventOrder) => void;
}

export const HistoryOrderCard: FC<
  {
    order: FeventOrder;
    /** 지금 응대 중인 상주 본인의 주문 — 강조 + "본인 주문" 배지 */
    isSameMember?: boolean;
    /** 같은 상가의 다른 상주 주문 — "다른 상주" 배지 (상주를 아는 화면에서만) */
    isOtherMember?: boolean;
  } & HistoryApplyHandlers
> = function HistoryOrderCard({
  order,
  isSameMember = false,
  isOtherMember = false,
  onApplyThanks,
  onApplyAddress,
}) {
  const chip = historyStatusChip(order);
  const address = order.bulkDeliveryAddress;
  const isBulk = order.deliveryMode === GiftOrderDeliveryMode.Bulk;
  // 감사카드 — 여러 장이던 주문은 카드들, 아니면 대표 1장 (첫 장이 문구 인쇄 스티커여도 나머지가 보이게)
  const thanksCards = pastThanksCards(order);
  const fullAddress = address?.address
    ? `${address.address} ${address.addressDetail ?? ''}`.trim()
    : '';

  return (
    <div
      className={`overflow-hidden rounded-lg border bg-white shadow-sm ${
        isSameMember
          ? 'border-emerald-300 ring-1 ring-emerald-100'
          : 'border-gray-200'
      }`}
    >
      {/* 헤더 — 상태·주문자·시각 */}
      <div className="flex items-start gap-2 border-b border-gray-100 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${chip.cls}`}>
              {chip.text}
            </span>
            {order.viaPhoneOrder && (
              <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700">
                전화주문
              </span>
            )}
            {/* 이름+전화번호 완전일치 = 이 상주 본인 / 아니면 같은 상가 다른 상주 */}
            {isSameMember && (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
                본인 주문
              </span>
            )}
            {isOtherMember && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500">
                다른 상주
              </span>
            )}
          </div>
          <p className="mt-1 text-sm font-semibold text-gray-900">
            {order.receiverName ?? '-'}
            <span className="ml-1.5 text-xs font-normal text-gray-500">
              {order.receiverPhoneNumber ?? ''}
            </span>
          </p>
        </div>
        <span className="whitespace-nowrap text-xs text-gray-400">
          {historyDateTime(new Date(order.createdAt))}
        </span>
      </div>

      {/* 상품 */}
      <div className="space-y-1 px-3 py-2">
        {order.orderDetails.map((detail, idx) => (
          <div key={idx} className="flex items-baseline justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm text-gray-800">
                {detail.giftItemEmbed?.name ?? '답례품'}
              </p>
              {(detail.selectedOptions ?? []).length > 0 && (
                <p className="text-xs text-gray-500">
                  {(detail.selectedOptions ?? [])
                    .map((option) => option.choiceName)
                    .join(', ')}
                </p>
              )}
            </div>
            <span className="whitespace-nowrap text-sm font-medium text-gray-900">
              {detail.quantity}개
            </span>
          </div>
        ))}
      </div>

      {/* 감사글·배송지 — 그대로 옮겨 담을 수 있게 복사 버튼 */}
      {(thanksCards.some((c) => c.message) || isBulk || order.deliveryMode) && (
        <div className="space-y-1.5 px-3 pb-2">
          {thanksCards.some((c) => c.message) && (
            <CopyableField
              //* 카드가 여러 장이던 주문(상품마다 디자인이 달랐던 주문)은 장수를 함께 — 복사는 감사글 있는 첫 카드
              label={thanksCards.length > 1 ? `감사글 · 카드 ${thanksCards.length}장` : '감사글'}
              copyText={thanksCards.find((c) => c.message)?.message ?? ''}
              onApply={onApplyThanks ? () => onApplyThanks(order) : undefined}
            >
              {thanksCards
                .filter((c) => c.message)
                .map((card, i) => (
                  <p
                    key={i}
                    className={`whitespace-pre-wrap ${i > 0 ? 'mt-1 border-t border-gray-100 pt-1' : ''}`}
                  >
                    {card.message}
                  </p>
                ))}
              {thanksCards[0]?.senderName && (
                <p className="mt-0.5 text-xs text-gray-500">{thanksCards[0].senderName}</p>
              )}
            </CopyableField>
          )}
          {isBulk && fullAddress ? (
            <CopyableField
              label="받는 주소"
              //* 주소만 복사(주문서 주소칸에 그대로 붙여넣기) — 이름·연락처는 별도 칸
              copyText={fullAddress}
              //* 우편번호가 없는 주문(구데이터)은 주문서 검증을 통과하지 못해 채우지 않는다
              onApply={
                onApplyAddress && address?.postalCode
                  ? () => onApplyAddress(order)
                  : undefined
              }
            >
              <p className="whitespace-pre-wrap">{fullAddress}</p>
              {address?.name && (
                <p className="mt-0.5 text-xs text-gray-500">
                  {address.name} {address.phoneNumber ?? ''}
                </p>
              )}
            </CopyableField>
          ) : (
            order.deliveryMode && (
              <p className="px-0.5 text-xs text-gray-500">
                {isBulk ? '일괄배송 (주소 미입력)' : '개별배송 — 받는 분이 각자 입력'}
              </p>
            )
          )}
        </div>
      )}

      {/* 결제금액 */}
      <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-3 py-2">
        <span className="text-xs text-gray-500">결제금액</span>
        <span className="text-sm font-bold text-gray-900">
          {formatPrice(order.paymentDetail.paymentByCash)}원
        </span>
      </div>
    </div>
  );
};

//* 행/패널에서 쓰는 이력 목록 (마운트 시 1회 조회)
//* layout: 'grid'는 넓은 화면(전화주문 관리 표 아래)용 2~3열, 'list'는 좁은 패널용 1열
export const FeventOrderHistory: FC<
  {
    feventId: string;
    layout?: 'list' | 'grid';
    /** 지금 보고 있는 상주 — 주면 본인 주문을 위로 올리고 표기를 구분한다 */
    memberName?: string;
    memberPhone?: string;
  } & HistoryApplyHandlers
> = function FeventOrderHistory({
  feventId,
  layout = 'list',
  memberName,
  memberPhone,
  onApplyThanks,
  onApplyAddress,
}) {
    const { isAdmin } = useAuth();
    const [orders, setOrders] = useState<FeventOrder[] | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
      let alive = true;
      (async () => {
        try {
          const data = await fetchFeventOrderHistory(feventId, isAdmin());
          if (alive) setOrders(data);
        } catch (err) {
          console.error(err);
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (alive)
            setErrorMessage(
              status === 404
                ? '주문 내역 조회가 아직 서버에 준비되지 않았습니다. 서버 업데이트 후 사용할 수 있습니다.'
                : '주문 내역을 불러오지 못했습니다.',
            );
        }
      })();
      return () => {
        alive = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feventId]);

    //* 본인(이름+전화번호 완전일치) 주문을 위로 — 각 묶음 안에서는 최신순(서버 정렬) 유지
    const { selfOrders, otherOrders } = useMemo(() => {
      const self: FeventOrder[] = [];
      const other: FeventOrder[] = [];
      (orders ?? []).forEach((order) => {
        if (isSameMemberOrder(order, memberName, memberPhone)) self.push(order);
        else other.push(order);
      });
      return { selfOrders: self, otherOrders: other };
    }, [orders, memberName, memberPhone]);

    //* 상주를 아는 화면에서만 본인/다른 상주를 구분해 표기한다
    const knowsMember = Boolean(memberName?.trim() && memberPhone?.trim());

    if (errorMessage) return <p className="py-2 text-sm text-red-600">{errorMessage}</p>;
    if (!orders)
      return (
        <div className="center-box py-4">
          <Loading />
        </div>
      );
    if (orders.length === 0)
      return (
        <p className="py-2 text-sm text-gray-500">
          이 상가로 만들어진 주문이 아직 없습니다.
        </p>
      );

    return (
      <div>
        <p className="mb-2 text-xs text-gray-500">
          {knowsMember ? (
            <>
              {memberName}님 주문 {selfOrders.length}건 · 같은 상가 다른 상주{' '}
              {otherOrders.length}건 (결제 시도 포함)
            </>
          ) : (
            <>이 상가(고인 기준)의 주문 {orders.length}건 — 다른 가족의 주문·결제 시도 포함</>
          )}
        </p>
        <div
          className={
            layout === 'grid'
              ? 'grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3'
              : 'space-y-2'
          }
        >
          {selfOrders.map((order) => (
            <HistoryOrderCard
              key={order._id}
              order={order}
              isSameMember={knowsMember}
              onApplyThanks={onApplyThanks}
              onApplyAddress={onApplyAddress}
            />
          ))}
          {/* 본인 주문과 다른 상주 주문 사이 구분선 (양쪽 다 있을 때만) */}
          {knowsMember && selfOrders.length > 0 && otherOrders.length > 0 && (
            <p className="col-span-full mt-1 border-t border-gray-200 pt-2 text-xs font-medium text-gray-400">
              여기부터 같은 상가 다른 상주의 주문
            </p>
          )}
          {otherOrders.map((order) => (
            <HistoryOrderCard
              key={order._id}
              order={order}
              isOtherMember={knowsMember}
              onApplyThanks={onApplyThanks}
              onApplyAddress={onApplyAddress}
            />
          ))}
        </div>
      </div>
    );
  };
